import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
const AdminFilter = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [candidateNric, setCandidateNric] = useState(""); // NRIC value
  const [batchList, setBatchList] = useState([]);
  const [candidateList, setCandidateList] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Helper to format date (YYYY-MM-DD)
  const formatDateForAPI = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const downloadPDF = () => {
    const pdf = new jsPDF("l", "mm", "a4"); // Landscape

    const candidate = candidateList.find(
      (c) => c.NricId === candidateNric
    );

    const candidateName = candidate?.Name || "";

    // Header
    pdf.setFontSize(18);
    pdf.text("Candidate MCQ Summary Report", 14, 15);

    pdf.setFontSize(11);

    pdf.text(`Candidate Name : ${candidateName}`, 14, 30);
    pdf.text(`NRIC : ${candidateNric}`, 14, 38);
    pdf.text(`Batch No : ${batchNo}`, 14, 46);
    pdf.text(`Course Start Date : ${startDate}`, 14, 54);
    pdf.text(`Course End Date : ${endDate}`, 14, 62);

    // Table
    autoTable(pdf, {
      startY: 75,

      head: [
        ["Question", "Correct Answer", "Selected Answer", "Marks"],
      ],

      body: uniqueResults.map((r) => [
        r.Question || "",
        r.Option_ans || "",
        r.Selectedanswer || "",
        r.Marks ?? ""
      ]),

      theme: "grid",

      margin: {
        left: 10,
        right: 10,
      },

      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        valign: "middle",
      },

      headStyles: {
        fillColor: [41, 128, 185],
        fontSize: 9,
        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 180 },
        1: { cellWidth: 35 },
        2: { cellWidth: 45 },
        3: { cellWidth: 20, halign: "center" },
      },

      didParseCell: function (data) {
        if (data.section === "body") {
          const row = uniqueResults[data.row.index];

          // Selected Answer column
          if (data.column.index === 2) {
            if (row.Option_ans === row.Selectedanswer) {
              data.cell.styles.textColor = [0, 128, 0]; // Green
              data.cell.styles.fontStyle = "bold";
            } else {
              data.cell.styles.textColor = [220, 53, 69]; // Red
              data.cell.styles.fontStyle = "bold";
            }
          }

          // Marks column
          if (data.column.index === 3) {
            const mark = Number(row.Marks);

            if (mark > 0) {
              data.cell.styles.textColor = [0, 128, 0]; // Green
              data.cell.styles.fontStyle = "bold";
            } else {
              data.cell.styles.textColor = [220, 53, 69]; // Red
              data.cell.styles.fontStyle = "bold";
            }
          }
        }
      },
    });

    // Footer Summary
    const finalY = pdf.lastAutoTable.finalY + 15;

    pdf.setFontSize(12);

    pdf.text(`Total Questions: ${totalQuestions}`, 14, finalY);

    pdf.text(
      `Total Marks Obtained: ${totalMarks}`,
      14,
      finalY + 8
    );

    pdf.text(
      `Percentage: ${percentage}%`,
      14,
      finalY + 16
    );

    pdf.text(
      `Status: ${isPassed ? "Pass" : "Fail"}`,
      14,
      finalY + 24
    );

    pdf.save(
      `${candidateName || "Candidate"}_MCQ_Report.pdf`
    );
  };
  // Fetch batch list
  useEffect(() => {
    const fetchBatchList = async () => {
      try {
        const response = await axios.get(
          "https://elearn-api.onrender.com/api/getBatchNo"
        );
        const data = response.data;
        if (Array.isArray(data)) {
          const batches = data.map((item) => item.BatchNo);
          setBatchList(batches);
        } else {
          setBatchList([]);
        }
      } catch (error) {
        console.error("Failed to fetch batch list:", error);
        setBatchList([]);
      }
    };
    fetchBatchList();
  }, []);

  // Fetch candidates for selected batch
  useEffect(() => {
    if (!batchNo) {
      setCandidateList([]);
      setCandidateNric("");
      return;
    }

    const fetchCandidates = async () => {
      try {
        const res = await axios.get(
          `https://elearn-api.onrender.com/api/candidates/${batchNo}`
        );

        setCandidateList(Array.isArray(res.data) ? res.data : []);
        setCandidateNric("");
      } catch (err) {
        console.error("Candidate list fetch error:", err);
        setCandidateList([]);
      }
    };

    fetchCandidates();
  }, [batchNo]);
  const uniqueResults = [
    ...new Map(
      results.map(item => [item.Question?.trim(), item])
    ).values()
  ];
  // Handle search
  const handleSearch = async () => {
    if (!startDate || !endDate || !batchNo || !candidateNric) {
      alert("Please fill out all filters");
      return;
    }

    const formattedStart = formatDateForAPI(startDate);
    const formattedEnd = formatDateForAPI(endDate);

    if (!formattedStart || !formattedEnd) {
      alert("Invalid date format");
      return;
    }

    setLoading(true);
    setError("");
    setResults([]);

    try {
      const url = `https://elearn-api.onrender.com/api/mcq-summary-admin?startDate=${encodeURIComponent(
        formattedStart
      )}&endDate=${encodeURIComponent(
        formattedEnd
      )}&batchNo=${batchNo}&nricId=${candidateNric}`;

      const res = await axios.get(url);

      if (!res.data || res.data.length === 0) {
        setError("No data found for selected candidate and batch.");
        setResults([]);
      } else {
        setResults(res.data);
        setError("");
      }
    } catch (err) {
      console.error("API error:", err);
      setError("No data found for selected candidate and batch.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Summary calculations
  const totalMarks = uniqueResults.reduce(
    (acc, curr) => acc + parseFloat(curr?.Marks || 0),
    0
  );
  const totalQuestions = uniqueResults.length;
  const percentage =
    totalQuestions > 0 ? ((totalMarks / totalQuestions) * 100).toFixed(2) : 0;
  const passPercentage = uniqueResults[0]?.Pass ? parseFloat(uniqueResults[0].Pass) : 0;
  const isPassed = parseFloat(percentage) >= passPercentage;

  return (
    <div className="admin-filter-container" >
      <h2>Candidate Summary - Admin</h2>
     
      <div className="filter-form">
        <div className="form-group">
          <label>Course Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Course End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Batch No:</label>
          <select value={batchNo} onChange={(e) => setBatchNo(e.target.value)}>
            <option value="">-- Select Batch --</option>
            {batchList.map((b, idx) => (
              <option key={idx} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Candidate:</label>
          <select
            value={candidateNric}
            onChange={(e) => setCandidateNric(e.target.value)}
            disabled={!candidateList.length}
          >
            <option value="">-- Select Candidate --</option>
            {candidateList.map((c, idx) => (
              <option key={idx} value={c.NricId}>
                {c.Name}
              </option>
            ))}
          </select>
        </div>

        <div
          className={`admin-btn ${loading ? "disabled" : ""}`}
          onClick={!loading ? handleSearch : undefined}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (!loading && (e.key === "Enter" || e.key === " ")) handleSearch();
          }}
        >
          {loading ? "Searching..." : "Search"}
        </div>
      </div>
       <div
        className="admin-btn"
        onClick={downloadPDF}
      >
        Download PDF
      </div>

      {error && <div className="error">{error}</div>}
      <div id="report-area">
        {results.length > 0 && (
          <>
            <table className="results-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Correct Answer</th>
                  <th>Selected Answer</th>
                  <th>Marks</th>
                </tr>
              </thead>
              <tbody>
                {uniqueResults.map((r, i) => (
                  <tr key={i}>
                    <td>{r.Question}</td>
                    <td>{r.Option_ans}</td>
                    <td className={r.Option_ans === r.Selectedanswer ? "correct" : "wrong"}>
                      {r.Selectedanswer}
                    </td>
                    <td>{r.Marks}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="summary-footer">
              <p>
                <strong>Total Questions:</strong> {totalQuestions}
              </p>
              <p>
                <strong>Total Marks Obtained:</strong> {totalMarks}
              </p>
              <p>
                <strong>Percentage:</strong> {percentage}%
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <span className={isPassed ? "pass" : "fail"}>
                  {isPassed ? "Pass" : "Fail"}
                </span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminFilter;
